import { AuthContext } from 'features/Auth/contexts/AuthContext';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getAccessLevel, getUserPerms, validateRoutePerms } from 'services/perms/perms.service';

const PermsContext = createContext();

// Provider component
export const PermsProvider = ({ children }) => {
  if (!children) return null;

  const authContext = useContext(AuthContext);
  const { accessToken } = authContext;
  const [data, setData] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [accessLevel, setAccessLevelState] = useState(null);
  const [maintenanceLevel, setMaintenanceLevelState] = useState(null);
  const [serviceManagerFlag, setServiceManagerFlagState] = useState(null);

  const fetchData = async () => {
    if (isFetching || (data == null && !accessToken)) return;
    if (isFetching || data) return;

    setIsFetching(true);

    // fetch both in parallel; order doesn’t matter for parsing
    const [accessResponse, permsResponse] = await Promise.all([
      getAccessLevel().catch(() => null),
      getUserPerms().catch(() => null),
    ]);

    const newAccessLevel = accessResponse?.data?.result?.group_id ?? null;
    if (newAccessLevel !== null) setAccessLevelState(newAccessLevel);

    if (permsResponse?.status === 200) {
      const raw = permsResponse.data?.perms;
      const permsData = typeof raw === 'string' ? JSON.parse(raw) : raw;
      setData(permsData ?? {});
    }

    setIsFetching(false);
  };

  // Try immediately
  useEffect(() => {
    if (accessToken) fetchData();
  }, [accessToken]);

  const get = () => {
    return data;
  };

  const setAccessLevel = (level) => {
    setAccessLevelState(level);
  };

  const setMaintenanceLevel = (level) => {
    setMaintenanceLevelState(level);
  };

  const setServiceManagerFlag = (flag) => {
    setServiceManagerFlagState(flag);
  };

  function testPermission(accessLevelList, permissions = [], testData = null) {
    //Check Access Level against provided list
    const accessLevelCheck =
      accessLevelList && Array.isArray(accessLevelList) && accessLevelList.length > 0
        ? accessLevelList.includes(Number(accessLevel))
        : true;

    let permissionsCheck;

    if (permissions.length > 0) {
      const permArr = permissions.map((permission) => {
        if (testData) {
          return testData[permission];
        }

        /**     (integrations and devices) **for now (this can)
        This defines exception permissions. These permissions aren't shown to the users.
            These permissions (in this case) return true if any permission that meets a naming scheme
            is true. This is to say "perm1 || perm2 || perm3 || etc..."
        */
        let filteredPerms;
        switch (permission) {
          case 'ViewConnections':
            filteredPerms = Object.keys(data)
              .filter((f) => {
                const regex = /^View[A-Za-z]+Connections$/;
                return regex.test(f);
              })
              .map((v) => v);
            return filteredPerms.length > 0
              ? filteredPerms.map((v) => data[v]).includes(true)
              : false;
          case 'Create-EditConnections':
            filteredPerms = Object.keys(data)
              .filter((f) => {
                const regex = /^Create-Edit[A-Za-z]+Connections$/;
                return regex.test(f);
              })
              .map((v) => v);
            return filteredPerms.length > 0
              ? filteredPerms.map((v) => data[v]).includes(true)
              : false;
          case 'DeleteConnections':
            filteredPerms = Object.keys(data)
              .filter((f) => {
                const regex = /^Delete[A-Za-z]+Connections$/;
                return regex.test(f);
              })
              .map((v) => v);
            return filteredPerms.length > 0
              ? filteredPerms.map((v) => data[v]).includes(true)
              : false;
          case 'ImportConnections':
            filteredPerms = Object.keys(data)
              .filter((f) => {
                const regex = /^Import[A-Za-z]+Connections$/;
                return regex.test(f);
              })
              .map((v) => v);
            return filteredPerms.length > 0
              ? filteredPerms.map((v) => data[v]).includes(true)
              : false;
          default:
            return data ? data[permission] : false;
        }
      });
      if (permArr.includes(undefined) || permArr.includes(null)) {
        return false;
      }
      permissionsCheck = !permArr.includes(false);
    } else if (permissions.length === 0) {
      permissionsCheck = accessLevelCheck;
    } else {
      permissionsCheck = false;
    }

    const finalResult = accessLevelCheck && permissionsCheck;
    return finalResult;
  }

  const validatePermissions = async (perms) => {
    try {
      const response = await validateRoutePerms(perms);
      return true;
    } catch (error) {
      if (error.response.status === 403) {
        return false;
      } else {
        return true;
      }
    }
  };

  const reset = () => {
    setData(null);
    setIsFetching(false);
    setAccessLevelState(null);
    setServiceManagerFlagState(null);
    setMaintenanceLevelState(null);
  };

  const isMaintenanceOperator = () => {
    const result = maintenanceLevel === 1 || accessLevel === 8;
    return result;
  };

  const isServiceManager = () => {
    const result = serviceManagerFlag === true && accessLevel === 12 ? true : false;
    return result;
  };

  const contextValue = useMemo(
    () => ({
      get,
      setAccessLevel,
      setMaintenanceLevel,
      testPermission,
      validatePermissions,
      isFetching,
      data,
      reset,
      accessLevel,
      maintenanceLevel,
      isMaintenanceOperator,
      serviceManagerFlag,
      isServiceManager,
      setServiceManagerFlag,
    }),
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [data, isFetching, accessLevel, maintenanceLevel],
  );
  return <PermsContext.Provider value={contextValue}>{children}</PermsContext.Provider>;
};

// Custom hook to use the context
export const usePerms = () => {
  const context = useContext(PermsContext);
  if (!context) {
    console.error('[perms] usePerms called outside of PermsProvider context!');
    throw new Error('usePerms must be used within a PermsProvider');
  }
  return context;
};

export { PermsContext };
